import React, { useEffect, useMemo, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import {
  FormGroup,
  Radio,
  SelectTrigger,
  ResourcePickerPopover,
} from "@jugl-web/ui-kit";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { TOPUP_OPTIONS } from "../GetExtraCreditsCard/consts";
import { ReactComponent as InfoIcon } from "./assets/info.svg";

interface AutoRechargeDialogContentProps {
  initialThreshold: number;
  initialCredits: number;
  planDefaultCredits: number;
  onChange: (threshold: number, credits: number) => void;
  onRequestSwitch?: () => void;
}

export const AutoRechargeDialogContent: React.FC<
  AutoRechargeDialogContentProps
> = ({
  initialThreshold,
  initialCredits,
  planDefaultCredits,
  onChange,
  onRequestSwitch,
}) => {
  const { t } = useTranslations();
  const { maxRollOverCredits } = useSubscriptionV2();
  const [thresholdInput, setThresholdInput] = useState(
    String(initialThreshold)
  );
  const [selectedCredits, setSelectedCredits] = useState(initialCredits);

  const threshold = Number(thresholdInput);
  const exceedsPlanCredits =
    planDefaultCredits > 0 && threshold >= planDefaultCredits;
  const isThresholdValid =
    thresholdInput.trim() !== "" &&
    !Number.isNaN(threshold) &&
    Number.isInteger(threshold) &&
    threshold > 0 &&
    !exceedsPlanCredits;

  useEffect(() => {
    onChange(isThresholdValid ? threshold : -1, selectedCredits);
  }, [threshold, selectedCredits, isThresholdValid, onChange]);

  const selectedOption = useMemo(
    () => TOPUP_OPTIONS.find((o) => o.credits === selectedCredits),
    [selectedCredits]
  );

  const totalPrice = selectedOption ? selectedOption.amountInCents / 100 : 0;

  const pickerItems = useMemo(
    () => [
      {
        id: "0",
        value: t({
          id: "cx-plans-page.auto-recharge-dialog.none",
          defaultMessage: "None",
        }),
      },
      ...TOPUP_OPTIONS.map((o) => ({
        id: String(o.credits),
        value: o.credits.toLocaleString(),
      })),
    ],
    [t]
  );

  return (
    <div className="flex flex-col gap-14 text-left">
      <div className="font-secondary text-sm leading-[21px] text-dark-700">
        {t(
          {
            id: "cx-plans-page.auto-recharge-dialog.description",
            defaultMessage:
              "When your credits drop below set limit, we'll automatically top up your account with <highlight>additional credits</highlight> to keep your service running smoothly. Please note that we roll over unused credits at the start of billing cycle (Up to {maxCredits} credits max)",
          },
          {
            highlight: (chunks: React.ReactNode) => (
              <span className="bg-gradient-to-r from-[#2196F3] to-[#8A41D3] bg-clip-text font-medium text-transparent">
                {chunks}
              </span>
            ),
            maxCredits: maxRollOverCredits.toLocaleString(),
          }
        )}
      </div>

      {onRequestSwitch && (
        <button
          type="button"
          onClick={onRequestSwitch}
          className="-mt-10 cursor-pointer self-start border-none bg-transparent p-0 font-secondary text-sm leading-[1.4] text-[#1045A1] underline"
        >
          {t({
            id: "cx-plans-page.auto-recharge-dialog.switch-to-extra-credits",
            defaultMessage: "Get Extra Credits now",
          })}
        </button>
      )}

      <div className="flex flex-col gap-4">
        {exceedsPlanCredits && (
          <div className="flex items-center gap-3 rounded-xl bg-[rgba(229,83,83,0.05)] p-3">
            <InfoIcon className="shrink-0" />
            <span className="font-secondary text-sm leading-[1.4] text-dark">
              {t(
                {
                  id: "cx-plans-page.auto-recharge-dialog.threshold-exceeds-plan",
                  defaultMessage:
                    "The target threshold should be less than {credits} of your available credits",
                },
                { credits: planDefaultCredits.toLocaleString() }
              )}
            </span>
          </div>
        )}
        <div className="relative pt-2.5">
          <div
            className={`flex h-[60px] items-center rounded-xl border border-solid bg-white px-5 ${
              !isThresholdValid && thresholdInput.trim() !== ""
                ? "border-gradients-danger"
                : "border-grey-400"
            }`}
          >
            <input
              type="text"
              inputMode="numeric"
              value={thresholdInput}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || /^\d+$/.test(val)) {
                  setThresholdInput(val.replace(/^0+(\d)/, "$1"));
                }
              }}
              className="w-full border-none bg-transparent font-secondary text-sm text-black outline-none"
            />
          </div>
          <div className="absolute top-0 left-3 bg-white px-2">
            <span className="font-secondary text-xs text-dark-600">
              {t({
                id: "cx-plans-page.auto-recharge-dialog.threshold-label",
                defaultMessage: "Recharge when credits fall below",
              })}
            </span>
          </div>
          {thresholdInput === "0" && (
            <span className="mt-1 font-secondary text-xs text-gradients-danger">
              {t({
                id: "cx-plans-page.auto-recharge-dialog.threshold-zero-error",
                defaultMessage: "Threshold must be greater than 0",
              })}
            </span>
          )}
        </div>

        <FormGroup
          label={t({
            id: "cx-plans-page.get-extra-credits-dialog.credits-amount",
            defaultMessage: "Credits amount",
          })}
          labelPosition="border-overlap"
        >
          <ResourcePickerPopover
            items={pickerItems}
            selectionBehavior={{ mode: "single", canToggle: false }}
            defaultSelectedIds={[String(selectedCredits)]}
            placement="bottom"
            onSelect={({ item, onClose }) => {
              setSelectedCredits(Number(item.id));
              onClose();
            }}
            renderLabel={({ value }) => value}
            renderEndIcon={(_item, _index, isSelected) => (
              <Radio isChecked={isSelected} readOnly />
            )}
            renderTrigger={({ Trigger, triggerRef, isOpen }) => {
              const selectedLabel =
                pickerItems.find((i) => i.id === String(selectedCredits))
                  ?.value ?? "";

              return (
                <Trigger
                  ref={triggerRef}
                  as={SelectTrigger}
                  label={selectedLabel}
                  isOpen={isOpen}
                  hasValue
                />
              );
            }}
          />
        </FormGroup>
      </div>

      {selectedCredits > 0 && (
        <div className="flex flex-col gap-6">
          <div className="h-px bg-dark-100" />
          <div className="flex items-start justify-between px-4">
            <div className="flex flex-col gap-1">
              <span className="font-secondary text-base leading-6 font-medium text-dark">
                {t({
                  id: "cx-plans-page.auto-recharge-dialog.total",
                  defaultMessage: "Total",
                })}
              </span>
              <span className="font-secondary text-sm leading-[19.6px] text-dark-700">
                {t({
                  id: "cx-plans-page.auto-recharge-dialog.charged-on-limit",
                  defaultMessage:
                    "Will be charged when credits reach the limit",
                })}
              </span>
            </div>
            <span className="font-secondary text-lg leading-[27px] font-semibold text-dark">
              ${totalPrice}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
